/**
 * 「时长 × 拍法 联动卡」—— 幕一内容层的核心(2026-09-13 A1，老板拍板设计稿 7d586023)。
 *
 * ═══ 为什么把时长和拍法放一张卡 ═══════════════════════════════════════════════════
 * 时长(装得下多少)与拍法(这条片怎么走的节拍)本是一件事的两面:选了长档、拍法节拍
 * 就该铺得开;拍法一换,节拍带跟着换。此前时长藏在「出成什么样」折叠里、拍法在目的横幅里、
 * 叙事节拍又只在横幅按 `arc` 画死四段 —— 三处各说各的,用户看不出它们联动。这张卡把它们收拢:
 *   ① 时长档 chips(值写 `form.briefDuration`，复用 `DURATION_OPTIONS` 单一映射);
 *   ② 拍法收起态(复用 `VideoFormulaPicker` compact:当前拍法名 / 未选时引擎默认那张 + 换拍法);
 *   ③ **节拍带按「当前拍法 × 当前时长档的 beats」画**(选中的 > 该档推荐牌;没有拍法回落目的 `arc`);
 *   ④ 合身度信号(A6:文案量 × 这个档能装多少,只提示不拦截)。
 *
 * ═══ 数据来源 ═══════════════════════════════════════════════════════════════════
 * taxonomy 走 `getCreativeTaxonomy` 现有 hook(与 VideoFormulaPicker / VideoFormatRow 同
 * queryKey/staleTime，缓存命中零多拉)。
 * ★B 批(2026-09-13)已接线「改时长→推荐拍法/节拍自动变」:后端 taxonomy 下发**分档**数据 ——
 * 卡上 `beatsByTier`(每档节拍表)+ 目的上 `defaultFormulaByTier`(每档引擎默认牌),二者都复用
 * 引擎既有逻辑投影(节拍缩放 / `select_formula` 按时长选卡),前端不硬编任何「档→拍法」映射。
 * `formulasForTier` 按当前档推荐牌重排(只排前不删),节拍带优先取 `beatsByTier[当前档]`。
 * 缺分档字段(老后端 schemaVersion 1)时回落 15s `beats` / 30s `defaultFormulaKey`,行为不塌。
 */

import type { BriefPurposeDef } from "@/entities/scene-card/model/briefPurpose";
import { cn } from "@/shared/lib/utils";
import type { Dispatch, SetStateAction } from "react";

import type { VideoFormulaCardDto } from "../../api/videoService";
import { useCreativeTaxonomy } from "../../model/useCreativeTaxonomy";
import { briefTargetSec } from "../../model/effectiveDuration";
import { durationFit } from "../../model/durationFit";
import type { FormState } from "../../model/videoCreateTypes";

import { DurationFitSignal } from "./DurationFitSignal";
import { VideoDurationTierRow } from "./VideoDurationTierRow";
import { humanizeBeat, VideoFormulaPicker } from "./VideoFormulaPicker";

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  /** 已选定的有效目的(锁定横幅那一份)—— 卡墙/节拍/合身度全按它取 taxonomy。 */
  purpose: BriefPurposeDef;
  /** 编排/出图进行中:整卡只读。 */
  disabled?: boolean;
  /**
   * D 批(2026-09-13):目的流两栏布局把时长拆成左栏独立卡、合身度搬进右栏摘要面板,
   * 所以这两块可各自关掉。缺省 true(单列 / 存量用法不变,单测无需传)。
   */
  showDuration?: boolean;
  showFit?: boolean;
}

/**
 * B 批接线(2026-09-13):「改时长 → 推荐拍法自动变」。后端 taxonomy 已下发分档数据
 * (`defaultFormulaByTier` = 每档引擎默认牌,复用 `select_formula` 的按时长选卡逻辑算出)。
 *
 * 这里按 `targetSec` 对应那档的推荐牌**重排**:推荐卡排最前、其余保持原序(稳定排序)——
 * **只重排不过滤**(不满足该档的卡降权到后面,但一张都不删,用户仍可手选,与拍法选择器
 * 「透镜只重排不过滤」同一口径)。**不硬编**任何「档→拍法」映射:推荐牌全部来自后端。
 * 缺分档数据(老后端 schemaVersion 1,`recommendedKey` 为空)时原样返回,不臆造顺序。
 */
function formulasForTier(
  formulas: VideoFormulaCardDto[] | undefined,
  targetSec: number | undefined,
  recommendedKey: string | null
): VideoFormulaCardDto[] | undefined {
  if (!formulas || targetSec == null || !recommendedKey) return formulas;
  return [...formulas]
    .map((c, i) => ({ c, i }))
    .sort(
      (a, b) =>
        (b.c.key === recommendedKey ? 1 : 0) - (a.c.key === recommendedKey ? 1 : 0) || a.i - b.i
    )
    .map(({ c }) => c);
}

export function VideoShootPlanCard({
  form,
  setForm,
  purpose,
  disabled,
  showDuration = true,
  showFit = true,
}: Props) {
  const taxonomyQuery = useCreativeTaxonomy(form.toneRegister);
  const purposeData =
    taxonomyQuery.data?.purposes?.find((p) => p.purpose === purpose.label) ?? null;

  const targetSec = briefTargetSec(form.briefDuration);
  // 该时长档的引擎推荐牌(B 批:后端 defaultFormulaByTier 按 select_formula 逐档算出);
  // 缺分档数据(老后端)时回落到 30s 那档的 defaultFormulaKey —— 二者对 "30" 同值。
  const tierKey = targetSec != null ? String(targetSec) : null;
  const recommendedKey =
    (tierKey ? purposeData?.defaultFormulaByTier?.[tierKey] : null) ??
    purposeData?.defaultFormulaKey ??
    null;
  const tierFormulas = formulasForTier(purposeData?.formulas, targetSec, recommendedKey);

  // 当前拍法 = 用户选中的那张 > 该档推荐牌(不选时按当前时长档,引擎会挑它)。
  // ★换时长档 → recommendedKey 变 → tierDefaultCard 变 → currentCard/节拍带自动重算。
  const pickedCard = tierFormulas?.find((c) => c.key === form.pickedFormulaKey) ?? null;
  const tierDefaultCard =
    (recommendedKey ? tierFormulas?.find((c) => c.key === recommendedKey) : null) ??
    tierFormulas?.find((c) => c.isDefault) ??
    null;
  const currentCard = pickedCard ?? tierDefaultCard;

  // ③ 节拍带:当前拍法在**当前时长档**的 beats(去 {槽位} 花括号)。优先取 beatsByTier[当前档],
  //    缺则回落 15s 的 beats,再缺(autoOnly / taxonomy 没下发 beats)回落目的 `arc`(按「→」切段)。
  const tierBeats = tierKey ? currentCard?.beatsByTier?.[tierKey] : undefined;
  const rawBeats = tierBeats?.length ? tierBeats : currentCard?.beats;
  const formulaBeats = rawBeats?.length ? rawBeats.map(humanizeBeat) : null;
  const arcBeats = purpose.arc
    .split("→")
    .map((s) => s.trim())
    .filter(Boolean);
  const beats = formulaBeats ?? arcBeats;
  const beatsFromFormula = formulaBeats != null;
  // 当前默认牌是否为「本时长档」的推荐(来自后端 defaultFormulaByTier,非老后端回落)——
  // 用户没手选时,节拍带下方说明按此标「Ns 推荐」,让「拍法随时长变」这件事被看见。
  const tierRecommended =
    !pickedCard &&
    tierKey != null &&
    currentCard != null &&
    purposeData?.defaultFormulaByTier?.[tierKey] === currentCard.key;

  // ④ 合身度(A6):当前目的×当前时长档的节拍摘要 + 用户输入量粗判,只提示不拦截。
  const beatPlan = targetSec
    ? (purposeData?.beatPlans?.find((b) => b.targetSec === targetSec) ?? null)
    : null;
  const fit = durationFit(form, beatPlan);

  return (
    <section
      data-video-shoot-plan
      className={cn(
        "space-y-3 rounded-[calc(var(--radius)*1.15)] border border-border/70 bg-card/60 px-5 py-4",
        disabled && "pointer-events-none opacity-60"
      )}
    >
      {/* ── ① 时长档(D 批:两栏布局把它拆到左栏独立卡时 showDuration=false)── */}
      {showDuration && <VideoDurationTierRow form={form} setForm={setForm} disabled={disabled} />}

      {/* ── ② 拍法(收起态复用 VideoFormulaPicker compact:单档 purpose,不跨叙事弧)── */}
      <VideoFormulaPicker
        compact
        purpose={purpose.label}
        register={form.toneRegister}
        value={form.pickedFormulaKey ?? null}
        disabled={disabled}
        onChange={(key, name) =>
          setForm((s) => ({ ...s, pickedFormulaKey: key, pickedFormulaName: name }))
        }
      />

      {/* ── ③ 节拍带(每拍一个色条)—— 随拍法变;没有拍法回落目的叙事弧 ──
          ★F 批:改 flex-wrap + 每块 min 7rem。旧版单行 `flex-1 min-w-0 line-clamp-2`,拍多
          (60s 达 11 拍)每块被挤到极窄、全是省略号且无处看全文。现在拍少(≤5)仍一行均分,
          拍多自然换到 2-3 行,每块 min-width 保证放得下,去掉 line-clamp 让序号+时间段+节拍名
          全文可读;窄屏靠 wrap 回落,不横向溢出。 */}
      <div>
        <div data-testid="shoot-plan-arc" className="flex flex-wrap gap-1.5">
          {beats.map((beat, i) => (
            <div
              key={`${i}-${beat}`}
              className="min-w-[7rem] flex-1 basis-[7rem] rounded-md border border-primary/20 bg-primary/5 px-2 py-1.5 text-2xs leading-snug text-foreground/80"
            >
              <span className="mr-1 font-mono text-[10px] text-primary/70">{i + 1}</span>
              <span className="align-middle">{beat}</span>
            </div>
          ))}
        </div>
        <p className="mt-1 text-2xs text-muted-foreground/80">
          {beatsFromFormula
            ? `按拍法「${currentCard?.name ?? ""}」的节拍走${
                pickedCard ? "" : tierRecommended ? `(${targetSec}s 推荐,可换)` : "(引擎默认,可换)"
              }`
            : "按这个目的的叙事弧(选一张拍法后按拍法节拍走)"}
        </p>
      </div>

      {/* ── ④ 合身度信号(A6):只提示,不禁用主 CTA;D 批两栏布局把它搬进右栏时 showFit=false ── */}
      {showFit && <DurationFitSignal fit={fit} setForm={setForm} disabled={disabled} />}
    </section>
  );
}
